import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Radio, CalendarDays, Clock, Youtube, Play, Share2, Linkedin, Facebook,
  Twitter, MessageCircle, Link2, Check, ArrowRight, Quote, Sparkles,
} from 'lucide-react';
import SEO from '@/components/SEO';

const PAGE_URL = 'https://iiypv.com/lyca-radio-interview';
const YT_WATCH = 'https://youtu.be/eTnjZdnmIMw';
const YT_EMBED = 'https://www.youtube.com/embed/eTnjZdnmIMw';
const YT_THUMB = 'https://i.ytimg.com/vi/eTnjZdnmIMw/maxresdefault.jpg';
const POSTER = 'https://iiypv.com/lyca-interview-poster.jpg';
const POSTER_LOCAL = '/lyca-interview-poster.jpg';
const VIDEO_SRC = '/media/lyca-radio-interview.mp4';

const VIDEO_NAME = 'IIY Phytoventra on Lyca Radio — Talking Points with Keith Vaz';
const VIDEO_DESC =
  'Jaz Sidhu (Rupinderjit Kaur), Co-Founder & Director of IIY Phytoventra, joins broadcaster Keith Vaz on Lyca Radio 1458 “Talking Points” to discuss proactive wellbeing, the heritage of turmeric, high-absorption curcumin (BCM-95®), and building a brand founded on trust, transparency and clear information.';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'VideoObject',
      name: VIDEO_NAME,
      description: VIDEO_DESC,
      thumbnailUrl: [YT_THUMB, POSTER],
      uploadDate: '2026-09-20T09:00:00+01:00',
      duration: 'PT11M42S',
      contentUrl: 'https://iiypv.com/media/lyca-radio-interview.mp4',
      embedUrl: YT_EMBED,
      publisher: {
        '@type': 'Organization',
        name: 'IIY Phytoventra Ltd',
        logo: {
          '@type': 'ImageObject',
          url: 'https://iiypv.com/og-image.jpg',
        },
      },
    },
    {
      '@type': 'NewsArticle',
      headline: 'IIY Phytoventra Featured on Lyca Radio: Co-Founder Jaz Sidhu on Talking Points with Keith Vaz',
      description: VIDEO_DESC,
      image: [YT_THUMB, POSTER],
      datePublished: '2026-09-20T09:00:00+01:00',
      dateModified: '2026-09-20T09:00:00+01:00',
      author: { '@type': 'Organization', name: 'IIY Phytoventra Ltd' },
      publisher: {
        '@type': 'Organization',
        name: 'IIY Phytoventra Ltd',
        logo: { '@type': 'ImageObject', url: 'https://iiypv.com/og-image.jpg' },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': PAGE_URL },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://iiypv.com/' },
        { '@type': 'ListItem', position: 2, name: 'News', item: 'https://iiypv.com/#press-releases' },
        { '@type': 'ListItem', position: 3, name: 'Lyca Radio Interview', item: PAGE_URL },
      ],
    },
  ],
};

const HIGHLIGHTS = [
  {
    q: 'Why turmeric — and why now?',
    a: 'Turmeric has been part of Indian kitchens and daily rituals for generations. The conversation explores how that heritage is now being met with modern clinical rigour — and why the form of curcumin you choose matters.',
  },
  {
    q: 'The absorption question',
    a: 'Ordinary curcumin is notoriously hard for the body to absorb. Jaz explains why IIY Phytoventra chose the clinically-researched BCM-95® extract, which is standardised for high bioavailability without synthetic additives.',
  },
  {
    q: 'Invest In Yourself',
    a: 'The brand philosophy — being proactive about everyday wellbeing rather than reactive — and why clear, honest information should sit at the heart of every wellness choice.',
  },
  {
    q: 'Rooted in trust & fair sourcing',
    a: 'From supporting the farmers and growers behind the raw botanicals to full transparency on what goes into every bottle, the segment covers the values IIY Phytoventra is built on.',
  },
];

function ShareBar() {
  const [copied, setCopied] = useState(false);
  const shareText = 'IIY Phytoventra on Lyca Radio — Talking Points with Keith Vaz. Watch the interview:';
  const enc = encodeURIComponent;
  const links = [
    { name: 'LinkedIn', Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(PAGE_URL)}`, cls: 'bg-[#0A66C2] hover:bg-[#095196]' },
    { name: 'Facebook', Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(PAGE_URL)}`, cls: 'bg-[#1877F2] hover:bg-[#1467d6]' },
    { name: 'X', Icon: Twitter, href: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(PAGE_URL)}`, cls: 'bg-black hover:bg-neutral-800' },
    { name: 'WhatsApp', Icon: MessageCircle, href: `https://wa.me/?text=${enc(shareText + ' ' + PAGE_URL)}`, cls: 'bg-[#25D366] hover:bg-[#1eb955]' },
  ];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(PAGE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard unavailable */ }
  };
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="share-bar">
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-900">
        <Share2 className="w-4 h-4" /> Share
      </span>
      {links.map(({ name, Icon, href, cls }) => (
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Share on ${name}`}
          data-testid={`share-${name.toLowerCase()}`}
          className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-white transition-colors ${cls}`}
        >
          <Icon className="w-4 h-4" />
        </a>
      ))}
      <button
        onClick={copy}
        data-testid="share-copy"
        className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-emerald-300 text-emerald-900 hover:bg-emerald-50 text-sm font-semibold transition-colors"
      >
        {copied ? <><Check className="w-4 h-4 text-emerald-600" /> Copied</> : <><Link2 className="w-4 h-4" /> Copy link</>}
      </button>
    </div>
  );
}

export default function LycaRadioInterview() {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const startVideo = () => {
    setPlaying(true);
    setTimeout(() => { if (videoRef.current) { videoRef.current.play?.(); } }, 50);
  };

  return (
    <div className="bg-white" data-testid="lyca-interview-page">
      <SEO
        title="IIY Phytoventra on Lyca Radio — Talking Points with Keith Vaz"
        description="Watch IIY Phytoventra Co-Founder Jaz Sidhu on Lyca Radio 1458 ‘Talking Points with Keith Vaz’ — turmeric heritage, high-absorption BCM-95® curcumin and the Invest In Yourself philosophy."
        keywords="IIY Phytoventra Lyca Radio, Keith Vaz turmeric, Jaz Sidhu, BCM-95 curcumin interview, Talking Points Lyca Radio"
        image={YT_THUMB}
        video={{ url: YT_EMBED, width: 1280, height: 720 }}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-emerald-950 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-400/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-10 md:pt-20 md:pb-14">
          <Link to="/" className="text-emerald-200/80 hover:text-white text-sm inline-flex items-center gap-1.5 mb-6" data-testid="back-home">
            ← Back to home
          </Link>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold bg-amber-400/15 text-amber-300 border border-amber-400/25">
            <Radio className="w-3.5 h-3.5" /> As heard on Lyca Radio 1458
          </span>
          <h1 className="mt-4 font-heading text-white text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
            IIY Phytoventra on <span className="text-amber-300">Talking Points</span> with Keith Vaz
          </h1>
          <p className="mt-4 max-w-2xl text-emerald-100/90 text-base md:text-lg leading-relaxed">
            Our Co-Founder &amp; Director <strong className="text-white">Jaz Sidhu (Rupinderjit Kaur)</strong> joins
            broadcaster <strong className="text-white">Keith Vaz</strong> to talk turmeric heritage, high-absorption
            curcumin and what it really means to <em>Invest In Yourself</em>.
          </p>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-emerald-100/90">
            <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-amber-300" /> Sunday, 20 September 2026</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-amber-300" /> Lyca Radio 1458 · Greater London</span>
          </div>
        </div>
      </section>

      {/* Player */}
      <section className="relative -mt-2 bg-gradient-to-b from-emerald-950 to-white pb-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-emerald-900/10 bg-black aspect-video relative" data-testid="interview-player">
            {!playing ? (
              <button
                onClick={startVideo}
                className="group absolute inset-0 w-full h-full"
                aria-label="Play interview"
                data-testid="play-button"
                style={{ backgroundImage: `url(${POSTER_LOCAL})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
              >
                <span className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-400 text-emerald-950 shadow-xl group-hover:scale-105 transition-transform">
                    <Play className="w-9 h-9 fill-emerald-950 ml-1" />
                  </span>
                </span>
              </button>
            ) : (
              <video
                ref={videoRef}
                src={VIDEO_SRC}
                poster={POSTER_LOCAL}
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="w-full h-full object-contain bg-black"
                data-testid="interview-video"
              />
            )}
          </div>

          <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <ShareBar />
            <a
              href={YT_WATCH}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="watch-youtube-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FF0000] hover:bg-[#d90000] text-white font-bold px-6 py-3 transition-colors"
            >
              <Youtube className="w-5 h-5" /> Watch on YouTube
            </a>
          </div>
        </div>
      </section>

      {/* Pull quote */}
      <section className="py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-2xl border border-emerald-100 bg-emerald-50/60 p-8 md:p-10"
            data-testid="pull-quote"
          >
            <Quote className="w-9 h-9 text-amber-400 mb-3" />
            <p className="font-heading text-2xl md:text-3xl text-emerald-950 leading-snug">
              “Investing in yourself starts with clear, honest information — so people can make confident, everyday choices about their wellbeing.”
            </p>
            <footer className="mt-4 text-sm text-emerald-800/80">
              — Jaz Sidhu (Rupinderjit Kaur), Co-Founder &amp; Director, IIY Phytoventra
            </footer>
          </motion.blockquote>
        </div>
      </section>

      {/* Highlights */}
      <section className="pb-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-4 h-4 text-emerald-700" />
            <span className="text-[11px] uppercase tracking-[0.24em] font-bold text-emerald-700">What they discussed</span>
          </div>
          <div className="grid md:grid-cols-2 gap-5" data-testid="highlights-grid">
            {HIGHLIGHTS.map((h, i) => (
              <motion.div
                key={h.q}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-emerald-300 hover:shadow-md transition-all"
              >
                <h3 className="font-heading text-lg font-bold text-emerald-950 mb-2">{h.q}</h3>
                <p className="text-[15px] text-muted-foreground leading-relaxed">{h.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About the guest + show */}
      <section className="py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-slate-200 p-6">
            <span className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700">The guest</span>
            <h3 className="font-heading text-xl font-bold text-emerald-950 mt-2 mb-2">Jaz Sidhu (Rupinderjit Kaur)</h3>
            <p className="text-[15px] text-muted-foreground leading-relaxed">
              Co-Founder &amp; Director of IIY Phytoventra. Jaz champions a science-meets-heritage
              approach to natural wellbeing — bringing clinically-researched botanicals like
              BCM-95® curcumin to UK households with full transparency on sourcing and quality.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 p-6">
            <span className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700">The show</span>
            <h3 className="font-heading text-xl font-bold text-emerald-950 mt-2 mb-2">Talking Points with Keith Vaz · Lyca Radio 1458</h3>
            <p className="text-[15px] text-muted-foreground leading-relaxed">
              Lyca Radio 1458 broadcasts across Greater London and beyond. “Talking Points”
              brings community, culture and wellbeing conversations to a wide UK audience.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="rounded-3xl bg-gradient-to-br from-emerald-800 to-emerald-950 p-8 md:p-12 text-center shadow-xl">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-white leading-tight">
              Start your Invest In Yourself journey
            </h2>
            <p className="mt-3 text-emerald-100/90 max-w-xl mx-auto text-base">
              Discover clinically-researched BCM-95® curcumin — vegan, allergen-free and made for an active life.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/products"
                data-testid="cta-shop"
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-emerald-950 font-bold px-7 py-3.5 transition-colors"
              >
                Shop BCM-95® <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/discovery-call"
                data-testid="cta-discovery"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white hover:bg-white/10 font-semibold px-7 py-3.5 transition-colors"
              >
                Book a free discovery call
              </Link>
            </div>
            <p className="mt-6 text-[11px] text-emerald-200/70 max-w-lg mx-auto">
              Food supplement. Not intended to diagnose, treat, cure or prevent any disease. Enjoy as part of a
              varied, balanced diet and healthy lifestyle.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
